import {
  type ProductVariant,
  type Cart,
} from "@shopify/hydrogen-react/storefront-api-types";

import { type DocumentNode } from "graphql";

import getCartUpsellsQuery from "@graphql/get-data-api-cart-upsells.graphql";
import {
  LEGACY_CART_API_URL,
  STOREFRONT_ACCESS_TOKEN,
  YOTPO_REVIEWS_API_URL,
} from "@tablescape/constants";
import { type UpsellsApiExperiment } from "@tablescape/state";
import {
  captureException,
  getFullyQualifiedUrl,
  type UpsellsExperimentInput,
  readUpsellExperimentInput,
  writeUpsellExperiment,
} from "@tablescape/utilities";

export type UpsellDataAPIRecommendation = {
  full_product_name: string;
  lift: number;
  variant_id: string;
};

export type UpsellDataAPIConnection = {
  apiAvailable: boolean;
  data: {
    cartUpsells: UpsellDataAPIRecommendation[];
    experiment?: UpsellsApiExperiment;
  };
};

const unavailableUpsellsApiConnection = (): UpsellDataAPIConnection => ({
  apiAvailable: false,
  data: { cartUpsells: [] },
});

const coerceUpsellVariantId = (variantId: string | number): string =>
  String(variantId);

/**
 * @type_assertion_justification
 * TECHNICAL REASON: JSON import types variant keys as a fixed union; API
 * variant ids are dynamic strings at runtime.
 * RUNTIME GUARANTEE: Missing keys fall back to the original variant id.
 * ALTERNATIVE CONSIDERED: keyof cast per lookup — rejected per TypeScript rules.
 * RISK ASSESSMENT: Unknown variant id passes through unchanged.
 * MIGRATION_PLAN: Replace with typed lookup if product-id-map gains runtime validation.
 */
type UNSAFE_ASSERTION__VariantIdMap = Record<
  string,
  { destinationId?: number }
>;

const lookupRemappedVariantId = (
  variants: unknown,
  variantId: string | number
): string | number => {
  const variantMap = variants as UNSAFE_ASSERTION__VariantIdMap;
  const key = coerceUpsellVariantId(variantId);

  return variantMap[key]?.destinationId ?? variantId;
};

async function normalizeCartUpsellsResponse(
  response: UpsellDataAPIConnection["data"]
): Promise<UpsellDataAPIConnection["data"]> {
  const cartUpsells = response.cartUpsells ?? [];

  if (process.env.STOREFRONT_ENV !== "staging") {
    return {
      ...response,
      cartUpsells: cartUpsells.map((upsell) => ({
        ...upsell,
        variant_id: coerceUpsellVariantId(upsell.variant_id),
      })),
    };
  }

  const productIdMap = await import("@root/scripts/product-id-map.json");

  return {
    ...response,
    cartUpsells: cartUpsells.map((upsell) => {
      const remappedVariantId = lookupRemappedVariantId(
        productIdMap.production.variants,
        upsell.variant_id
      );

      return {
        ...upsell,
        variant_id: coerceUpsellVariantId(remappedVariantId),
      };
    }),
  };
}

export const dataAPIFetcher = async ([url, cartId, variantIds]: [
  string,
  Cart["id"],
  number[]
]) => {
  let resolvedVariantIds = variantIds;

  if (process.env.STOREFRONT_ENV === "staging") {
    const productIdMap = await import("@root/scripts/product-id-map.json");

    resolvedVariantIds = variantIds.map((variantId) => {
      const remapped = lookupRemappedVariantId(
        productIdMap.staging.variants,
        variantId
      );

      return typeof remapped === "number" ? remapped : variantId;
    });
  }

  const experimentInput = readUpsellExperimentInput();
  const variables: {
    cart: { id: Cart["id"]; lines: number[] };
    exp?: UpsellsExperimentInput;
  } = {
    cart: { id: cartId, lines: resolvedVariantIds },
  };

  if (experimentInput) {
    variables.exp = experimentInput;
  }

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        query: getCartUpsellsQuery,
        variables,
      }),
      mode: "cors",
      credentials: "same-origin",
    });

    if (!response.ok) {
      return unavailableUpsellsApiConnection();
    }

    const body = (await response.json()) as {
      data?: UpsellDataAPIConnection["data"];
    };
    const data = body.data ?? { cartUpsells: [] };

    if (data.experiment) {
      writeUpsellExperiment(data.experiment);
    }

    return {
      apiAvailable: true,
      data: await normalizeCartUpsellsResponse(data),
    };
  } catch (err) {
    captureException(err instanceof Error ? err : new Error(String(err)), {
      tags: {
        "issue.type": "fetch",
      },
    });

    return unavailableUpsellsApiConnection();
  }
};

type StorefrontAPIParameters = [
  string,
  DocumentNode,
  Record<string, string | string[] | { key: string; value: string }[]>
];

export const storefrontAPIFetcher = async ([
  url,
  query,
  variables,
]: StorefrontAPIParameters) =>
  fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": STOREFRONT_ACCESS_TOKEN,
    },
    body: JSON.stringify({
      query,
      variables,
    }),
  })
    .then((res) => res.json())
    .catch((err) =>
      captureException(err, {
        tags: {
          "issue.type": "fetch",
        },
      })
    );

export const multiStorefrontAPIFetcher = (
  paramsArray: StorefrontAPIParameters[]
) => {
  return Promise.all(paramsArray.map((params) => storefrontAPIFetcher(params)));
};

type YotpoReviewsAPIParameters = [string, DocumentNode, Record<string, string>];

export const yotpoReviewsAPIFetcher = async ([
  appKey,
  productId,
  params = {},
]: YotpoReviewsAPIParameters) =>
  fetch(
    getFullyQualifiedUrl(
      `${YOTPO_REVIEWS_API_URL}/${appKey}/products/${productId}/reviews.json`,
      params
    )
  )
    .then((res) => res.json())
    .catch((err) =>
      captureException(err, {
        tags: {
          "issue.type": "fetch",
        },
      })
    );

export const multiYotpoReviewsAPIFetcher = (
  paramsArray: YotpoReviewsAPIParameters[]
) => {
  return Promise.all(
    paramsArray.map((params) => yotpoReviewsAPIFetcher(params))
  );
};

type LoopAPIParameters = [url: string, variantIds: ProductVariant["id"][]];

export const loopAPIFetcher = async ([url, variantIds]: LoopAPIParameters) =>
  fetch(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-Authorization": window.__LOOP_RETURNS_API_KEY__,
    },
    body: JSON.stringify({
      cart: variantIds,
    }),
  }).then((res) => res.json());

export type LegacyCartAPIParameters = [
  url: string,
  items:
    | {
        id: ProductVariant["id"];
        quantity: number;
      }[]
    | undefined,
  method?: "GET" | "POST"
];

export const legacyCartAPIFetcher = async ([
  url,
  items,
  method = "POST",
]: LegacyCartAPIParameters) => {
  return fetch(url, {
    method,
    headers: {
      "Content-Type": "application/json",
    },
    body: items ? JSON.stringify(items) : undefined,
  }).then((res) => res.json());
};

export const legacyCartMutateAPIFetcher = async ([
  url,
  items,
  method = "POST",
]: LegacyCartAPIParameters) => {
  return fetch(url, {
    method,
    headers: {
      "Content-Type": "application/json",
    },
    body: items ? JSON.stringify(items) : undefined,
  }).then(() => fetch(LEGACY_CART_API_URL).then((res) => res.json()));
};
